import { ticketTemplates } from './ticketTemplates';
import { simpleEscpos } from './simpleEscpos';

/**
 * Servicio para renderizar tickets en diferentes formatos
 */
export const ticketRenderer = {
  /**
   * Renderiza un ticket en formato de texto plano
   * @param {Object} template - Template del ticket
   * @param {string} type - Tipo de ticket (delivery, fulfillment, backup, test)
   * @returns {string} - Ticket en formato de texto plano
   */
  renderPlainText(template, type) {
    let output = '';
    
    switch (type) {
      case 'delivery':
        output = this._renderDeliveryPlainText(template);
        break;
      case 'fulfillment':
        output = this._renderFulfillmentPlainText(template);
        break;
      case 'backup':
        output = this._renderBackupPlainText(template);
        break;
      case 'test':
        output = this._renderTestPlainText(template);
        break;
      default:
        output = this._renderDeliveryPlainText(template);
    }
    
    return output;
  },
  
  /**
   * Renderiza un ticket en formato ESC/POS (base64)
   * @param {Object} template - Template del ticket
   * @param {string} type - Tipo de ticket (delivery, fulfillment, backup, test)
   * @returns {string} - Ticket en formato ESC/POS (base64)
   */
  renderESCPOS(template, type) {
    let bytes = [];
    
    // Inicializar impresora
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.INIT);
    
    switch (type) {
      case 'delivery':
        bytes = this._renderDeliveryESCPOS(bytes, template);
        break;
      case 'fulfillment':
        bytes = this._renderFulfillmentESCPOS(bytes, template);
        break;
      case 'backup':
        bytes = this._renderBackupESCPOS(bytes, template);
        break;
      case 'test':
        bytes = this._renderTestESCPOS(bytes, template);
        break;
      default:
        bytes = this._renderDeliveryESCPOS(bytes, template);
    }
    
    // Cortar papel
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.CUT);
    
    // Convertir a base64
    return simpleEscpos.bytesToBase64(bytes);
  },
  
  /**
   * Genera un ticket según el tipo y formato especificados
   * @param {Object} options - Opciones para generar el ticket
   * @param {string} type - Tipo de ticket (delivery, fulfillment, backup, test)
   * @param {string} format - Formato de salida (plain, escpos)
   * @returns {string} - Ticket en el formato especificado
   */
  generateTicket(options, type = 'delivery', format = 'plain') {
    let template;
    
    // Obtener el template según el tipo
    switch (type) {
      case 'delivery':
        template = ticketTemplates.deliveryTicket(options);
        break;
      case 'fulfillment':
        template = ticketTemplates.fulfillmentTicket(options);
        break;
      case 'backup':
        template = ticketTemplates.backupTicket(options);
        break;
      case 'test':
        template = ticketTemplates.testTicket(options.business);
        break;
      default:
        template = ticketTemplates.deliveryTicket(options);
    }
    
    // Renderizar según el formato
    if (format === 'escpos') {
      return this.renderESCPOS(template, type);
    } else {
      return this.renderPlainText(template, type);
    }
  },
  
  // Métodos privados para renderizar en texto plano
  
  /**
   * Renderiza un ticket de entrega en texto plano
   * @private
   */
  _renderDeliveryPlainText(template) {
    let output = '';
    
    // Encabezado
    output += '================================\n';
    output += `         ${template.header.businessName}        \n`;
    output += '================================\n\n';
    output += `VENTA #${template.header.saleNumber}\n`;
    output += `${template.header.date}\n`;
    output += '--------------------------------\n';
    
    // Tabla de productos
    output += 'PRODUCTO      CANT    PRECIO    TOTAL\n';
    output += '--------------------------------\n';
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      output += `${productName}${quantity}${unitPrice}${total}\n`;
    });
    
    // Totales
    output += '--------------------------------\n';
    output += `SUBTOTAL:                $${template.totals.subtotal.toFixed(2)}\n`;
    
    if (template.totals.depositAmount > 0) {
      output += `SEÑA ENVASES:            $${template.totals.depositAmount.toFixed(2)}\n`;
    }
    
    output += `TOTAL:                     $${template.totals.total.toFixed(2)}\n`;
    
    // Cliente
    if (template.customer) {
      output += '--------------------------------\n';
      output += `CLIENTE: ${template.customer.name}\n`;
      if (template.customer.phone) {
        output += `TELÉFONO: ${template.customer.phone}\n`;
      }
      if (template.customer.address) {
        output += `DIRECCIÓN: ${template.customer.address}\n`;
      }
    }
    
    // Pie de página
    output += '================================\n';
    output += `${template.footer.thankYouMessage}\n`;
    output += `${template.footer.website}\n`;
    output += '================================\n\n\n\n';
    
    return output;
  },
  
  /**
   * Renderiza un ticket de preparación en texto plano
   * @private
   */
  _renderFulfillmentPlainText(template) {
    let output = '';
    
    // Encabezado
    output += '================================\n';
    output += `         ${template.header.businessName}        \n`;
    output += '================================\n\n';
    output += `VENTA #${template.header.saleNumber}\n`;
    output += `${template.header.date}\n`;
    output += '--------------------------------\n';
    
    // Tabla de productos
    output += 'PRODUCTO                  CANTIDAD\n';
    output += '--------------------------------\n';
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(25);
      const quantity = item.quantity.toString();
      output += `${productName}${quantity}\n`;
    });
    
    // Notas
    if (template.notes) {
      output += '--------------------------------\n';
      output += 'NOTAS:\n';
      output += `${template.notes}\n`;
    }
    
    // Pie de página
    output += '================================\n';
    output += `${template.footer.thankYouMessage}\n`;
    output += `${template.footer.website}\n`;
    output += '================================\n\n\n\n';
    
    return output;
  },
  
  /**
   * Renderiza un ticket de respaldo en texto plano
   * @private
   */
  _renderBackupPlainText(template) {
    let output = '';
    
    // Encabezado
    output += '================================\n';
    output += `         ${template.header.businessName}        \n`;
    output += '================================\n\n';
    output += `VENTA #${template.header.saleNumber}\n`;
    output += `${template.header.date}\n`;
    output += '--------------------------------\n';
    
    // Tabla de productos
    output += 'PRODUCTO      CANT    PRECIO    TOTAL\n';
    output += '--------------------------------\n';
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      output += `${productName}${quantity}${unitPrice}${total}\n`;
    });
    
    // Totales
    output += '--------------------------------\n';
    output += `SUBTOTAL:                $${template.totals.subtotal.toFixed(2)}\n`;
    
    if (template.totals.depositAmount > 0) {
      output += `SEÑA ENVASES:            $${template.totals.depositAmount.toFixed(2)}\n`;
    }
    
    output += `TOTAL:                     $${template.totals.total.toFixed(2)}\n`;
    
    // Cliente
    if (template.customer) {
      output += '--------------------------------\n';
      output += `CLIENTE: ${template.customer.name}\n`;
      if (template.customer.phone) {
        output += `TELÉFONO: ${template.customer.phone}\n`;
      }
      if (template.customer.address) {
        output += `DIRECCIÓN: ${template.customer.address}\n`;
      }
    }
    
    // Información de pago
    if (template.payment) {
      output += '--------------------------------\n';
      output += `MÉTODO DE PAGO: ${template.payment.method}\n`;
      if (template.payment.reference) {
        output += `REFERENCIA: ${template.payment.reference}\n`;
      }
    }
    
    // Pie de página
    output += '================================\n';
    output += `${template.footer.thankYouMessage}\n`;
    output += `${template.footer.website}\n`;
    output += '================================\n\n\n\n';
    
    return output;
  },
  
  /**
   * Renderiza un ticket de prueba en texto plano
   * @private
   */
  _renderTestPlainText(template) {
    let output = '';
    
    // Encabezado
    output += '================================\n';
    output += `${template.title}\n`;
    output += `${template.businessName}\n`;
    output += '================================\n\n';
    output += `Business ID: ${template.businessId}\n`;
    output += `Fecha: ${template.date}\n`;
    output += '--------------------------------\n';
    
    // Tabla de productos
    output += 'PRODUCTO      CANT    PRECIO    TOTAL\n';
    output += '--------------------------------\n';
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      output += `${productName}${quantity}${unitPrice}${total}\n`;
    });
    
    // Totales
    output += '--------------------------------\n';
    output += `SUBTOTAL:                $${template.totals.subtotal.toFixed(2)}\n`;
    output += `TOTAL:                     $${template.totals.total.toFixed(2)}\n\n`;
    
    // Pie de página
    output += `${template.footer.thankYouMessage}\n`;
    output += `${template.footer.website}\n\n\n`;
    
    return output;
  },
  
  // Métodos privados para renderizar en ESC/POS
  
  /**
   * Renderiza un ticket de entrega en ESC/POS
   * @private
   */
  _renderDeliveryESCPOS(bytes, template) {
    // Encabezado
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.businessName}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('================================\n'));
    
    // Información de la venta
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`VENTA #${template.header.saleNumber}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.date}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Tabla de productos
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${productName}${quantity}${unitPrice}${total}\n`));
    });
    
    // Totales
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_RIGHT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`SUBTOTAL:             $${template.totals.subtotal.toFixed(2)}\n`));
    
    if (template.totals.depositAmount > 0) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`SEÑA ENVASES:         $${template.totals.depositAmount.toFixed(2)}\n`));
    }
    
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`TOTAL:                $${template.totals.total.toFixed(2)}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    
    // Cliente
    if (template.customer) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
      bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`CLIENTE: ${template.customer.name}\n`));
      if (template.customer.phone) {
        bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`TELÉFONO: ${template.customer.phone}\n`));
      }
      if (template.customer.address) {
        bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`DIRECCIÓN: ${template.customer.address}\n`));
      }
    }
    
    // Pie de página
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.FEED, [1]);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.thankYouMessage}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.website}\n\n\n`));
    
    return bytes;
  },
  
  /**
   * Renderiza un ticket de preparación en ESC/POS
   * @private
   */
  _renderFulfillmentESCPOS(bytes, template) {
    // Encabezado
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.businessName}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('================================\n'));
    
    // Información de la venta
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`VENTA #${template.header.saleNumber}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.date}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Tabla de productos
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('PRODUCTO                  CANTIDAD\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(25);
      const quantity = item.quantity.toString();
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${productName}${quantity}\n`));
    });
    
    // Notas
    if (template.notes) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('NOTAS:\n'));
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.notes}\n`));
    }
    
    // Pie de página
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.FEED, [1]);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.thankYouMessage}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.website}\n\n\n`));
    
    return bytes;
  },
  
  /**
   * Renderiza un ticket de respaldo en ESC/POS
   * @private
   */
  _renderBackupESCPOS(bytes, template) {
    // Encabezado
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.businessName}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('================================\n'));
    
    // Información de la venta
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`VENTA #${template.header.saleNumber}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.header.date}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Tabla de productos
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${productName}${quantity}${unitPrice}${total}\n`));
    });
    
    // Totales
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_RIGHT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`SUBTOTAL:             $${template.totals.subtotal.toFixed(2)}\n`));
    
    if (template.totals.depositAmount > 0) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`SEÑA ENVASES:         $${template.totals.depositAmount.toFixed(2)}\n`));
    }
    
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`TOTAL:                $${template.totals.total.toFixed(2)}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    
    // Cliente
    if (template.customer) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
      bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`CLIENTE: ${template.customer.name}\n`));
      if (template.customer.phone) {
        bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`TELÉFONO: ${template.customer.phone}\n`));
      }
      if (template.customer.address) {
        bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`DIRECCIÓN: ${template.customer.address}\n`));
      }
    }
    
    // Información de pago
    if (template.payment) {
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
      bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`MÉTODO DE PAGO: ${template.payment.method}\n`));
      if (template.payment.reference) {
        bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`REFERENCIA: ${template.payment.reference}\n`));
      }
    }
    
    // Pie de página
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.FEED, [1]);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.thankYouMessage}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.website}\n\n\n`));
    
    return bytes;
  },
  
  /**
   * Renderiza un ticket de prueba en ESC/POS
   * @private
   */
  _renderTestESCPOS(bytes, template) {
    // Encabezado
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.title}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.businessName}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('================================\n\n'));
    
    // Información
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_LEFT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`Business ID: ${template.businessId}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`Fecha: ${template.date}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Tabla de productos
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_ON);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('PRODUCTO      CANT    PRECIO    TOTAL\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.BOLD_OFF);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    
    // Productos
    template.items.forEach(item => {
      const productName = item.name.padEnd(14);
      const quantity = item.quantity.toString().padEnd(4);
      const unitPrice = item.unitPrice.toFixed(2).padEnd(8);
      const total = item.total.toFixed(2);
      bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${productName}${quantity}${unitPrice}${total}\n`));
    });
    
    // Totales
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes('--------------------------------\n'));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_RIGHT);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`SUBTOTAL:             $${template.totals.subtotal.toFixed(2)}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`TOTAL:                $${template.totals.total.toFixed(2)}\n\n`));
    
    // Pie de página
    bytes = simpleEscpos.concat(bytes, simpleEscpos.COMMANDS.ALIGN_CENTER);
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.thankYouMessage}\n`));
    bytes = simpleEscpos.concat(bytes, simpleEscpos.textToBytes(`${template.footer.website}\n\n\n`));
    
    return bytes;
  },
}; 